/**
 * Creates a debounced function that delays invoking func until after wait milliseconds have elapsed
 * since the last time the debounced function was invoked.
 * 
 * @param func The function to debounce
 * @param wait The number of milliseconds to delay
 * @param options Options object or boolean for immediate execution
 * @returns The debounced function with an additional cancel method
 */
export interface DebouncedFunction<T extends (...args: unknown[]) => unknown> {
  (...args: Parameters<T>): ReturnType<T> | undefined;
  cancel: () => void;
}

interface DebounceOptions {
  leading?: boolean;
  trailing?: boolean;
}

export function debounce<T extends (...args: unknown[]) => unknown>(
  func: T,
  wait = 300,
  options: DebounceOptions | boolean = { leading: false, trailing: true }
): DebouncedFunction<T> {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  let lastCallTime: number | undefined;

  // Convert boolean immediate parameter to options object
  const opts = typeof options === 'boolean' 
    ? { leading: options, trailing: true }
    : { leading: false, trailing: true, ...options };

  const debounced = function(this: unknown, ...args: Parameters<T>) {
    const context = this;
    const now = Date.now();

    if (!lastCallTime && opts.leading === false) {
      lastCallTime = now;
    }

    const remaining = wait - (now - (lastCallTime || 0));

    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = undefined;
    }

    if (remaining <= 0 || remaining > wait) {
      lastCallTime = now;
      return func.apply(context, args);
    } else if (opts.trailing !== false) {
      timeoutId = setTimeout(() => {
        lastCallTime = opts.leading === false ? 0 : Date.now();
        timeoutId = undefined;
        func.apply(context, args);
      }, remaining);
    }

    return undefined;
  } as DebouncedFunction<T>;

  debounced.cancel = function() {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = undefined;
    }
    lastCallTime = undefined;
  };

  return debounced;
} 